import { useState, useEffect } from "react";
import { Chip, useTheme } from "@mui/material";
import AiCanvas from "@/ai/AiCanvas";

/**
 * AiCanvases — Container that renders tab pills for each canvas
 * and shows the currently selected AiCanvas underneath.
 *
 * Props:
 *  - canvasUpdates: Array<{ id, name?, timestamp }>
 *  - newCanvasData: string | null  (live streamed markdown)
 *  - canvasLoading: boolean
 *  - onChange: (markdown: string) => void
 *  - onLoad: (canvas: object) => void
 *  - onUpdate: (canvas: object) => void
 */
export default function AiCanvases({
    canvasUpdates = [],
    newCanvasData,
    canvasLoading,
    onChange,
    onLoad,
    onUpdate,
    onCanvasSelected,
}) {
    const theme = useTheme();
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (canvasUpdates.length > 0 && activeIndex >= canvasUpdates.length) {
            setActiveIndex(canvasUpdates.length - 1);
        }
    }, [canvasUpdates.length]);

    // Auto-select the newest canvas when a new one is created
    useEffect(() => {
        if (canvasUpdates.length > 0) {
            setActiveIndex(canvasUpdates.length - 1);
        }
    }, [canvasUpdates.length]);

    const activeCanvas = canvasUpdates[activeIndex] || null;

    // Notify parent whenever the active canvas changes (creation, load, or tab switch)
    useEffect(() => {
        onCanvasSelected(activeCanvas);
    }, [activeCanvas?.id]);

    return (
        <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
            {canvasUpdates.length > 1 && (
                <div
                    style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 8,
                        flexWrap: "wrap",
                    }}
                >
                    {canvasUpdates.map((canvas, index) => (
                        <Chip
                            key={canvas.id}
                            label={canvas.name || `Canvas ${index + 1}`}
                            size="small"
                            variant={
                                index === activeIndex ? "filled" : "outlined"
                            }
                            color={
                                index === activeIndex ? "primary" : "default"
                            }
                            onClick={() => setActiveIndex(index)}
                            clickable
                            sx={{
                                fontWeight: index === activeIndex ? 600 : 400,
                                transition: "all 0.2s ease",
                            }}
                        />
                    ))}
                </div>
            )}

            <div style={{ flexGrow: 1, overflow: "auto" }}>
                <AiCanvas
                    canvasUpdate={activeCanvas}
                    newCanvasData={newCanvasData}
                    newLoadingState={canvasLoading}
                    onChange={onChange}
                    onLoad={onLoad}
                    onUpdate={onUpdate}
                />
            </div>
        </div>
    );
}
